import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import MobileStepper from "@material-ui/core/MobileStepper";
import Typography from "@material-ui/core/Typography";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import { BRAND_NAME } from "@constants";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { NUMBER_OFFER_MAX, ROUTES } from "@constants";
import { useAddOffer } from "@hooks/useAddOffer";
import { useCreateProspect } from "@hooks/useCreateProspect";
import { useGetCart } from "@hooks/useGetCart";
import { useOffers } from "@hooks/useOffers";
import { getOrderAuthorization, OrderAuthorizationEnum } from "@utils/getOrderAuthorization";
import { clearStorage, getProspectId } from "@utils/storage";
import { HomeOffer } from "./HomeOffer";
import { ICart, ICheckEligibilityResponse, IOffer } from "../cart/ICart";
import { fixUrlLanguage } from "@utils/changeLanguage";
import { CHECK_ELIGIBILITY, getCartCrossSellRoute } from "@api-routes";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";
import { useFetch } from "@common/hooks/useFetch";
import { ICartResponse, getResponseToState } from "@mappers/getCartMapper";

export const HomeOfferList = () => {
    const { arrow, offerList, offerListState, getOfferList } = useOffers();
    const { userCart, userCartState, getCart } = useGetCart();
    const { createProspectState, callCreateProspect } = useCreateProspect();
    const { addOfferState, addOffer } = useAddOffer();
    const { t } = useTranslation("home");
    const [orderAuthorization, setOrderAuthorization] = useState<OrderAuthorizationEnum | undefined>();
    const { push } = useHistory();
    const [checkEligibilityState, { get }] = useFetch();

    const checkEligibility = () => {
        get<ICheckEligibilityResponse>(CHECK_ELIGIBILITY, { isSecured: true }).then((checkEligibilityResponse) => {
            if (checkEligibilityResponse && !checkEligibilityResponse.eligible) {
                switch (checkEligibilityResponse.reason) {
                    case "IN_COLLECTION":
                        setOrderAuthorization(OrderAuthorizationEnum.InCollection);
                        break;
                    case "PENDING_ACQUISITION":
                        setOrderAuthorization(OrderAuthorizationEnum.PendingAcquisition);
                        break;
                    case "PENDING_CROSS_SELL":
                        setOrderAuthorization(OrderAuthorizationEnum.PendingCrossSell);
                        break;
                    case "MAX_SUBSCRIPTIONS_REACHED":
                        setOrderAuthorization(OrderAuthorizationEnum.TooMuchOfferCrossSell);
                        break;
                }
            } else {
                if (!getProspectId()) {
                    setOrderAuthorization(getOrderAuthorization(userCart));
                } else {
                    get<ICartResponse>(getCartCrossSellRoute()).then((userCartResponse) => {
                        console.log("userCartResponse: ", userCartResponse);
                        if (userCartResponse) {
                            setOrderAuthorization(getOrderAuthorization(getResponseToState(userCartResponse)));
                        }
                    });
                }
            }
        });
    };

    useEffect(() => {
        if (keycloakUtils.isAuthenticated()) {
            const prospectId: string = getProspectId();
            if (prospectId && prospectId.includes("-")) {
                clearStorage();
            }
            checkEligibility();
        } else {
            setOrderAuthorization(getOrderAuthorization(userCart));
        }

        //setStopRefresh(true);
    }, [userCart]);

    const handleOfferClick = (selectedOffer: IOffer) => {
        const addOfferCall = (prospect: string) => {
            addOffer(selectedOffer.catalogOfferCode, selectedOffer.catalogTariffPlanCode, false, prospect).then(
                (newCart?: ICart) => {
                    if (newCart) {
                        push(fixUrlLanguage(ROUTES.CART_PAGE_URL_EN));
                    }
                }
            );
        };

        const prospectId = getProspectId();
        if (prospectId) {
            addOfferCall(prospectId);
        } else {
            callCreateProspect().then((prospect) => {
                if (prospect?.prospectUuid) {
                    addOfferCall(prospect.prospectUuid);
                }
            });
        }
    };

    const offerListToDisplay = offerList.slice(
        offerList.length < arrow.arrowStep ? offerList.length : arrow.arrowStep,
        arrow.arrowStep + 1
    );

    const nextButton = (
        <Button
            color="inherit"
            disabled={arrow.arrowStep + 1 >= offerList.length}
            size="small"
            onClick={arrow.handleNext}
        >
            <KeyboardArrowRight fontSize="large" />
        </Button>
    );

    const backButton = (
        <Button color="inherit" disabled={arrow.arrowStep === 0} size="small" onClick={arrow.handleBack}>
            <KeyboardArrowLeft fontSize="large" />
        </Button>
    );

    const isLoadingList = [
        offerListState.isLoading,
        userCartState.isLoading,
        createProspectState.isLoading,
        addOfferState.isLoading,
        checkEligibilityState.isLoading,
    ];
    const isLoading = isLoadingList.some((loading) => loading);
    const errorOptions = [
        offerListState.error,
        userCartState.error,
        createProspectState.error,
        addOfferState.error,
        checkEligibilityState.error,
    ];
    const error = errorOptions.find((isError) => isError);

    let retry;

    if (offerListState.error) {
        retry = getOfferList;
    }

    if (userCartState.error) {
        retry = getCart;
    }

    if (createProspectState.error) {
        retry = callCreateProspect;
    }

    if (checkEligibilityState.error) {
        retry = checkEligibility;
    }

    if (addOfferState.error) {
        retry = () => window.location.reload();
    }

    return (
        <div className="offer-home">
            <HandleErrorAndLoading error={error} isHomePage isLoading={isLoading} retryFetch={retry}>
                {orderAuthorization === OrderAuthorizationEnum.OrderOk && (
                    <Fragment>
                        <Grid className="offer-home__carousel" container>
                            {offerListToDisplay.map((offer, index) => (
                                <Grid item key={`${offer.catalogOfferCode}-${index}`} xs={12}>
                                    <HomeOffer handleOfferClick={handleOfferClick} offer={offer} />
                                </Grid>
                            ))}
                        </Grid>
                        {offerListToDisplay.length > 1 && (
                            <Grid container justify="center">
                                <Grid item sm={5} xs={7}>
                                    <MobileStepper
                                        activeStep={arrow.arrowStep}
                                        backButton={backButton}
                                        className="offer-home-stepper"
                                        classes={{
                                            dotActive: "offer-home-stepper__activeDot",
                                        }}
                                        nextButton={nextButton}
                                        position="static"
                                        steps={offerList.length}
                                        variant="dots"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Fragment>
                )}
                {orderAuthorization === OrderAuthorizationEnum.TooMuchOffer && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("tooMuchOffer", { max: NUMBER_OFFER_MAX })}
                            </Typography>
                        </Grid>
                        <Grid item md={3} sm={4} xs={6}>
                            <Button
                                color="secondary"
                                fullWidth
                                size="large"
                                variant="contained"
                                onClick={() => push(fixUrlLanguage(ROUTES.CART_PAGE_URL_EN))}
                            >
                                {t("goCart")}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {orderAuthorization === OrderAuthorizationEnum.TooMuchOfferCrossSell && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("tooMuchOffer", { max: NUMBER_OFFER_MAX })}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {orderAuthorization === OrderAuthorizationEnum.InCollection && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("inCollection")}
                            </Typography>
                        </Grid>
                        <Grid item sm={4} xs={6}>
                            <Button
                                color="secondary"
                                fullWidth
                                href={process.env.MYACCOUNT_URL}
                                size="large"
                                variant="contained"
                            >
                                {t("goMyAccount", { brand: BRAND_NAME })}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {orderAuthorization === OrderAuthorizationEnum.PendingAcquisition && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("pendingAcquisition")}
                            </Typography>
                        </Grid>
                        <Grid item sm={4} xs={6}>
                            <Button
                                color="secondary"
                                fullWidth
                                href={process.env.MYACCOUNT_URL}
                                size="large"
                                variant="contained"
                            >
                                {t("goMyAccount", { brand: BRAND_NAME })}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {orderAuthorization === OrderAuthorizationEnum.PendingCrossSell && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("pendingCrossSell")}
                            </Typography>
                        </Grid>
                        <Grid item sm={4} xs={6}>
                            <Button
                                color="secondary"
                                fullWidth
                                href={process.env.MYACCOUNT_URL}
                                size="large"
                                variant="contained"
                            >
                                {t("goMyAccount", { brand: BRAND_NAME })}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {orderAuthorization === OrderAuthorizationEnum.OrderImpossible && (
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography
                                align="center"
                                className="offer-home__error-max-offer"
                                color="inherit"
                                gutterBottom
                                variant="body1"
                            >
                                {t("tooMuchOffer", { max: NUMBER_OFFER_MAX })}
                            </Typography>
                        </Grid>
                        <Grid item sm={4} xs={6}>
                            <Button
                                color="secondary"
                                fullWidth
                                href={process.env.MYACCOUNT_URL}
                                size="large"
                                variant="contained"
                            >
                                {t("goMyAccount", { brand: BRAND_NAME })}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </HandleErrorAndLoading>
        </div>
    );
};
